import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PeriodStatusBadge } from './PeriodStatusBadge'
import { Period } from '@/types/period'
import { formatPeriodDisplay } from '@/utils/periodHelpers'
import { cn } from '@/lib/utils'

interface PeriodCardProps {
  period: Period
  onClick: () => void
}

/**
 * Card component displaying a salary period with status and CTA button.
 * Shows period name, date range, and status badge.
 */
export function PeriodCard({ period, onClick }: PeriodCardProps) {
  const isLocked = period.status === 'locked'

  // Format dates for display
  const startDateFormatted = format(period.startDate.toDate(), 'd MMM', { locale: sv })
  const endDateFormatted = format(period.endDate.toDate(), 'd MMM yyyy', { locale: sv })
  const dateRange = `${startDateFormatted} - ${endDateFormatted}`

  // Period name: use custom name or generate from period ID
  const periodName = period.name || formatPeriodDisplay(period.id)

  return (
    <Card
      className={cn(
        'transition-colors',
        isLocked && 'border-muted bg-muted/30'
      )}
    >
      <CardContent className="pt-6">
        <div className="flex flex-col gap-4">
          {/* Header: Status badge */}
          <div className="flex items-center justify-between">
            <PeriodStatusBadge status={period.status} />
          </div>

          {/* Period info */}
          <div className="space-y-1">
            <h3 className="text-lg font-semibold capitalize">{periodName}</h3>
            <p className="text-sm text-muted-foreground">{dateRange}</p>
          </div>

          {/* CTA Button */}
          <Button
            onClick={onClick}
            variant={isLocked ? 'outline' : 'default'}
            className="w-full"
          >
            Fortsätt lönekörning
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
